import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { BsShieldLock, BsSearch, BsCheckAll } from 'react-icons/bs';
import { FiSave, FiRefreshCw, FiSlash } from 'react-icons/fi';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import { Button, Badge } from '../../Components/UI';
import { groupedPlanSections, PLAN_SECTION_VALUES } from '../../constants/planSections';

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

// Roles a MainDoctor may assign. MainDoctor and admin/SuperAdmin are absent on
// purpose: this screen must not mint an account that equals or outranks the one
// using it.
const ASSIGNABLE_ROLES = ['Doctor', 'AssistantDoctor', 'Consultant', 'Receptionist'];

const MANAGER_ROLES = ['MainDoctor', 'admin', 'Admin', 'MasterAdmin', 'SuperAdmin'];

// Roles that hold every section by definition. They are not listed or editable
// here — there is nothing to grant them and nothing safe to take away.
const FULL_ACCESS_ROLES = ['MainDoctor', 'admin', 'Admin', 'MasterAdmin', 'SuperAdmin'];

const fetchOrgUsers = async () => {
  const res = await axios.get(`${END_POINT}/configAccess/users/getOrgUsers/?uid=all`, authHeaders());
  return Array.isArray(res?.data) ? res.data : [];
};

const fetchUserAccess = async (username) => {
  const res = await axios.get(`${END_POINT}/configAccess/${encodeURIComponent(username)}`, authHeaders());
  return res?.data || {};
};

const saveUserAccess = async (username, body) => {
  const res = await axios.put(`${END_POINT}/configAccess/${encodeURIComponent(username)}`, body, authHeaders());
  return res?.data || {};
};

const errorText = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  return data?.error || data?.message || error?.message || fallback;
};

export default function Permissions() {
  const loggedInUser = useSelector((state) => state?.AuthReducer?.userLogindata?.data);
  const canManage = MANAGER_ROLES.includes(loggedInUser?.role);

  // The subscription plan is the outer bound: per-user permissions can only
  // narrow what the clinic already pays for, never extend it. Anything outside
  // the plan is therefore un-tickable here and stripped on save.
  const planSections = loggedInUser?.plan?.sections;

  // When the plan is not on the token there is nothing to bound against, so fall
  // back to allowing everything rather than locking the whole screen.
  const planKnown = Array.isArray(planSections) && planSections.length > 0;
  const isInPlan = useCallback(
    (value) => !planKnown || planSections.includes(value),
    [planKnown, planSections]
  );

  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [search, setSearch] = useState('');

  const [selectedUsername, setSelectedUsername] = useState('');
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const groups = useMemo(() => groupedPlanSections(), []);

  // Sections outside the subscription plan are not shown at all — the plan is
  // the outer bound, so there is nothing useful a MainDoctor could do with them.
  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => ({ ...group, sections: group.sections.filter((s) => isInPlan(s.value)) }))
        .filter((group) => group.sections.length > 0),
    [groups, isInPlan]
  );

  const loadUsers = useCallback(() => {
    setIsLoadingUsers(true);
    fetchOrgUsers()
      .then((all) => {
        const list = all.filter((u) => !FULL_ACCESS_ROLES.includes(u.role));
        setUsers(list);
        setSelectedUsername((current) =>
          list.some((u) => u.username === current) ? current : list?.[0]?.username || ''
        );
      })
      .catch((error) => toast.error(errorText(error, 'Failed to load users')))
      .finally(() => setIsLoadingUsers(false));
  }, []);

  useEffect(() => {
    if (canManage) loadUsers();
  }, [canManage, loadUsers]);

  useEffect(() => {
    if (!selectedUsername) return;

    let cancelled = false;
    setIsLoadingAccess(true);

    fetchUserAccess(selectedUsername)
      .then((record) => {
        if (cancelled) return;
        const next = {
          role: record?.role || '',
          active: record?.active !== false,
          permissions: Array.isArray(record?.permissions) ? record.permissions : [],
        };
        setSaved(next);
        setDraft(next);
      })
      .catch((error) => {
        if (cancelled) return;
        setSaved(null);
        setDraft(null);
        toast.error(errorText(error, 'Failed to load permissions'));
      })
      .finally(() => !cancelled && setIsLoadingAccess(false));

    return () => {
      cancelled = true;
    };
  }, [selectedUsername]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      [u.username, u.email, u.role].some((v) => String(v || '').toLowerCase().includes(term))
    );
  }, [users, search]);

  const isDirty = useMemo(() => {
    if (!draft || !saved) return false;
    // Grants the plan no longer covers count as dirty on their own, otherwise
    // Save stays disabled and there is no way to clear them.
    if (draft.permissions.some((p) => !isInPlan(p))) return true;
    return (
      draft.role !== saved.role ||
      draft.active !== saved.active ||
      draft.permissions.length !== saved.permissions.length ||
      draft.permissions.some((p) => !saved.permissions.includes(p))
    );
  }, [draft, saved, isInPlan]);

  const togglePermission = (value) => {
    if (!isInPlan(value)) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const has = prev.permissions.includes(value);
      return {
        ...prev,
        permissions: has ? prev.permissions.filter((p) => p !== value) : [...prev.permissions, value],
      };
    });
  };

  const setGroup = (sections, enabled) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const values = sections.map((s) => s.value);
      const without = prev.permissions.filter((p) => !values.includes(p));
      // Select-all only reaches as far as the plan does.
      const granted = values.filter(isInPlan);
      return { ...prev, permissions: enabled ? [...without, ...granted] : without };
    });
  };

  const handleSave = async () => {
    if (!draft || !selectedUsername) return;

    setIsSaving(true);
    try {
      // The endpoint writes role, permissions and active together, so all three
      // go back even when only one changed.
      // Drop anything the plan no longer covers, so a downgrade cannot leave
      // stale grants behind on the record.
      const permissions = draft.permissions.filter(isInPlan);

      const record = await saveUserAccess(selectedUsername, {
        role: draft.role,
        permissions,
        active: draft.active,
      });

      const next = {
        role: record?.role ?? draft.role,
        active: record?.active !== false,
        permissions: Array.isArray(record?.permissions) ? record.permissions : permissions,
      };
      setSaved(next);
      setDraft(next);
      setUsers((prev) =>
        prev.map((u) => (u.username === selectedUsername ? { ...u, role: next.role, active: next.active } : u))
      );
      toast.success('Permissions updated');
    } catch (error) {
      toast.error(errorText(error, 'Failed to save permissions'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canManage) {
    return (
      <RightWrapper>
        <div className="mx-auto mt-10 max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <FiSlash className="mx-auto h-8 w-8 text-rose-500" />
          <h2 className="mt-3 text-base font-semibold text-slate-900">Permissions are managed by the Main Doctor</h2>
          <p className="mt-1 text-sm text-slate-500">
            Your account does not have access to this screen.
          </p>
        </div>
      </RightWrapper>
    );
  }

  const grantedCount = draft?.permissions?.filter(isInPlan).length || 0;
  const availableCount = planKnown
    ? PLAN_SECTION_VALUES.filter(isInPlan).length
    : PLAN_SECTION_VALUES.length;
  // Grants left over from a plan downgrade. They do nothing, and Save clears them.
  const staleGrants = draft?.permissions?.filter((p) => !isInPlan(p)) || [];

  return (
    <RightWrapper>
      <div className="w-full p-4 md:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
              <BsShieldLock className="h-4 w-4" />
            </span>
            <div>
              <h1 className="text-lg font-bold leading-tight text-slate-900">Permissions</h1>
              <p className="text-xs text-slate-500">
                Choose what each member of your clinic can open. The Main Doctor always has full access.
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            icon={<FiRefreshCw className="h-3.5 w-3.5" />}
            onClick={loadUsers}
            isLoading={isLoadingUsers}
          >
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[18rem_1fr]">
          {/* Users */}
          <aside className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:sticky lg:top-4">
            <div className="relative mb-3">
              <BsSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff..."
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
              />
            </div>

            {isLoadingUsers && <p className="px-2 py-6 text-center text-xs text-slate-400">Loading staff...</p>}

            {!isLoadingUsers && filteredUsers.length === 0 && (
              <p className="px-2 py-6 text-center text-xs text-slate-400">
                No staff to manage yet. Add a doctor, consultant or receptionist first.
              </p>
            )}

            <ul className="space-y-1">
              {filteredUsers.map((user) => {
                const isSelected = user.username === selectedUsername;
                return (
                  <li key={user.user_id || user.username}>
                    <button
                      type="button"
                      onClick={() => setSelectedUsername(user.username)}
                      className={`w-full rounded-xl border px-3 py-2 text-left transition ${
                        isSelected
                          ? 'border-sky-300 bg-sky-50'
                          : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-semibold text-slate-800">{user.username}</span>
                        {user.active === false && <Badge variant="danger" size="sm">Off</Badge>}
                      </div>
                      <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                        {user.role || 'No role'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Sections */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            {!selectedUsername && (
              <p className="p-10 text-center text-sm text-slate-400">Pick a staff member to edit their access.</p>
            )}

            {selectedUsername && isLoadingAccess && (
              <p className="p-10 text-center text-sm text-slate-400">Loading permissions...</p>
            )}

            {selectedUsername && !isLoadingAccess && !draft && (
              <p className="p-10 text-center text-sm text-slate-400">
                No permission record exists for <b>{selectedUsername}</b>.
              </p>
            )}

            {selectedUsername && !isLoadingAccess && draft && (
              <>
                <div className="sticky top-0 z-10 flex flex-wrap items-end justify-between gap-3 rounded-t-2xl border-b border-slate-100 bg-white/95 p-4 backdrop-blur">
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        Role
                      </label>
                      <select
                        value={draft.role}
                        onChange={(e) => setDraft((prev) => ({ ...prev, role: e.target.value }))}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                      >
                        {/* An existing role outside the assignable list still has to
                            show, otherwise saving would silently change it. */}
                        {!ASSIGNABLE_ROLES.includes(draft.role) && draft.role && (
                          <option value={draft.role}>{draft.role}</option>
                        )}
                        {ASSIGNABLE_ROLES.map((role) => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    <label className="flex cursor-pointer items-center gap-2 pb-1.5">
                      <input
                        type="checkbox"
                        checked={draft.active}
                        onChange={(e) => setDraft((prev) => ({ ...prev, active: e.target.checked }))}
                        className="h-4 w-4 cursor-pointer accent-emerald-600"
                      />
                      <span className="text-xs font-semibold text-slate-600">Account active</span>
                    </label>

                    <span className="pb-1.5 text-xs text-slate-500">
                      <b className="text-slate-800">{grantedCount}</b> / {availableCount} sections
                      {planKnown && <span className="text-slate-400"> in plan</span>}
                    </span>
                  </div>

                  <Button
                    size="sm"
                    icon={<FiSave className="h-3.5 w-3.5" />}
                    onClick={handleSave}
                    isLoading={isSaving}
                    disabled={!isDirty}
                  >
                    {isDirty ? 'Save changes' : 'Saved'}
                  </Button>
                </div>

                {staleGrants.length > 0 && (
                  <p className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                    <b>{staleGrants.length}</b> saved {staleGrants.length === 1 ? 'section is' : 'sections are'} no
                    longer in your subscription plan and {staleGrants.length === 1 ? 'grants' : 'grant'} nothing.
                    Saving will remove {staleGrants.length === 1 ? 'it' : 'them'}.
                  </p>
                )}

                <div className="space-y-6 p-4">
                  {visibleGroups.map((group) => {
                    const allOn = group.sections.every((s) => draft.permissions.includes(s.value));
                    return (
                      <div key={group.title}>
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            {group.title}
                          </h3>
                          <button
                            type="button"
                            onClick={() => setGroup(group.sections, !allOn)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 hover:text-sky-700"
                          >
                            <BsCheckAll className="h-3.5 w-3.5" />
                            {allOn ? 'Clear all' : 'Select all'}
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                          {group.sections.map((section) => {
                            const checked = draft.permissions.includes(section.value);

                            return (
                              <label
                                key={section.value}
                                className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2 transition ${
                                  checked
                                    ? 'border-sky-200 bg-sky-50/70'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePermission(section.value)}
                                  className="mt-0.5 h-4 w-4 cursor-pointer accent-sky-600"
                                />
                                <span className="min-w-0 text-xs font-medium text-slate-800">
                                  {section.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {visibleGroups.length === 0 && (
                    <p className="py-10 text-center text-sm text-slate-400">
                      Your subscription plan does not include any sections to assign.
                    </p>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </RightWrapper>
  );
}
